import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { Shell } from "@/components/Shell";
import { BottomSheet } from "@/components/BottomSheet";
import { DOC_META, money, fmtDate } from "@/lib/format";
import { Plus, FolderOpen, FileText, ChevronRight, Check } from "lucide-react";

const Input = ({ testid, ...p }) => (
  <input data-testid={testid} {...p} className="w-full bg-surfaceSecondary border border-border rounded-[12px] px-4 py-3 text-base text-onSurface focus:outline-none focus:border-brandPrimary placeholder:text-muted-foreground" />
);

// Selectable document row for the "add to folder" step
const PickRow = ({ d, selected, onToggle }) => {
  const m = DOC_META[d.type] || {};
  return (
    <button data-testid={`pick-doc-${d.id}`} onClick={onToggle} className={`w-full rounded-[12px] border p-3 flex items-center gap-3 text-left transition-colors ${selected ? "border-brandPrimary bg-brandTertiary/40" : "border-border bg-surface"}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${selected ? "bg-brandPrimary" : "border-2 border-borderStrong"}`}>{selected && <Check size={14} className="text-onBrand" />}</div>
      <div className="flex-1 min-w-0">
        <p className="font-heading font-bold text-sm text-onSurface truncate">{d.number} · {d.data?.title || m.label}</p>
        <p className="text-xs text-muted-foreground truncate">{d.client?.name || "No client"} · {fmtDate(d.created_at)}</p>
      </div>
      {d.data?.total != null && d.type !== "waste_note" && <span className="text-sm font-heading font-bold text-onSurface">{money(d.data.total)}</span>}
    </button>
  );
};

export default function Folders() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });

  // step 2: pick documents to add to the folder just created
  const [picking, setPicking] = useState(null); // { client, docs, selected:Set }
  const [addingBusy, setAddingBusy] = useState(false);

  const load = () => api.get("/clients").then(({ data }) => setClients(data.clients)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const createFolder = async () => {
    if (!form.name.trim()) { toast.error("Enter a client name"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", { name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() });
      const { data: docData } = await api.get("/documents");
      setSheet(false); setForm({ name: "", phone: "", email: "" });
      setPicking({ client: data.client, docs: docData.documents, selected: new Set() });
    } catch { toast.error("Could not create folder"); } finally { setSaving(false); }
  };

  const toggle = (id) => {
    setPicking((p) => { const s = new Set(p.selected); s.has(id) ? s.delete(id) : s.add(id); return { ...p, selected: s }; });
  };

  const addSelected = async () => {
    const ids = [...picking.selected];
    setAddingBusy(true);
    try {
      await Promise.all(ids.map((id) => api.patch(`/documents/${id}`, { client_id: picking.client.id })));
      toast.success(ids.length ? `Added ${ids.length} to ${picking.client.name}` : "Folder created");
      const cid = picking.client.id; setPicking(null); load(); navigate(`/jobs/${cid}`);
    } catch { toast.error("Couldn't add documents"); } finally { setAddingBusy(false); }
  };

  return (
    <Shell back title="Folders" subtitle={`${clients.length} client folder${clients.length === 1 ? "" : "s"}`} right={
      <button data-testid="new-folder-btn" onClick={() => setSheet(true)} className="w-10 h-10 rounded-full bg-brandPrimary text-onBrand flex items-center justify-center active:scale-90 transition-transform"><Plus size={22} /></button>
    }>
      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-[68px] bg-surfaceTertiary rounded-[12px] animate-pulse" />)}</div>
      ) : clients.length === 0 ? (
        <div className="bg-surface rounded-[16px] border border-dashed border-borderStrong p-10 text-center mt-4">
          <div className="w-12 h-12 rounded-full bg-brandTertiary flex items-center justify-center mx-auto mb-3"><FolderOpen size={22} className="text-brandSecondary" /></div>
          <p className="font-heading font-bold text-onSurface">No folders yet</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Make a folder for a client and drop their quotes, invoices & notes inside.</p>
          <button data-testid="empty-new-folder" onClick={() => setSheet(true)} className="bg-brandPrimary text-onBrand rounded-full px-5 h-11 font-heading font-bold text-sm active:scale-95 transition-transform">Create a folder</button>
        </div>
      ) : (
        <div className="space-y-2 pb-4 animate-fade-up">
          {clients.map((c) => (
            <button key={c.id} data-testid={`folder-${c.id}`} onClick={() => navigate(`/jobs/${c.id}`)} className="w-full bg-surface rounded-[12px] border border-border p-4 flex items-center gap-3 hover:border-brandPrimary transition-colors active:scale-[0.99] text-left">
              <div className="w-11 h-11 rounded-[12px] bg-brandTertiary flex items-center justify-center shrink-0"><FolderOpen size={20} className="text-brandSecondary" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm text-onSurface truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1"><FileText size={12} /> {c.document_count} document{c.document_count === 1 ? "" : "s"}</p>
              </div>
              <ChevronRight size={18} className="text-borderStrong" />
            </button>
          ))}
        </div>
      )}

      {/* Step 1: create folder */}
      <BottomSheet open={sheet} onClose={() => setSheet(false)} title="New folder" testid="new-folder-sheet">
        <div className="flex flex-col gap-3">
          <Input testid="folder-name" placeholder="Client name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          <Input testid="folder-phone" placeholder="Phone (optional)" type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input testid="folder-email" placeholder="Email (optional)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <button data-testid="folder-create" disabled={!form.name.trim() || saving} onClick={createFolder} className="mt-1 bg-brandPrimary text-onBrand rounded-full h-12 font-heading font-bold active:scale-[0.98] transition-transform disabled:opacity-40">
            {saving ? "Creating…" : "Create & choose documents"}
          </button>
        </div>
      </BottomSheet>

      {/* Step 2: pick documents to add */}
      <BottomSheet open={!!picking} onClose={() => { const cid = picking?.client?.id; setPicking(null); if (cid) navigate(`/jobs/${cid}`); }} title={picking ? `Add to ${picking.client.name}` : ""} testid="pick-docs-sheet">
        {picking && (
          <>
            <p className="text-sm text-muted-foreground mb-3">Tap any documents to add to this folder. You can skip and add them later.</p>
            {picking.docs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">You haven't made any documents yet.</p>
            ) : (
              <div className="space-y-2 max-h-[46vh] overflow-y-auto no-scrollbar">
                {picking.docs.map((d) => <PickRow key={d.id} d={d} selected={picking.selected.has(d.id)} onToggle={() => toggle(d.id)} />)}
              </div>
            )}
            <button data-testid="folder-add-selected" disabled={addingBusy} onClick={addSelected} className="w-full mt-4 bg-brandPrimary text-onBrand rounded-full h-12 font-heading font-bold active:scale-[0.98] transition-transform disabled:opacity-50">
              {addingBusy ? "Adding…" : picking.selected.size ? `Add ${picking.selected.size} document${picking.selected.size === 1 ? "" : "s"}` : "Done"}
            </button>
          </>
        )}
      </BottomSheet>
    </Shell>
  );
}
